<h2 mat-dialog-title>
    <mat-icon class="title-icon entity-users">person</mat-icon>
    <span class="title-text-desktop">{{data.user?.name || data.node?.data?.name || 'Staff Member'}}</span>
    <span class="title-text-phone">Staff member</span>
    <button mat-icon-button mat-dialog-close class="close-button">
        <mat-icon>close</mat-icon>
    </button>
</h2>

<mat-dialog-content class="user-details-content">
    <div class="user-details-layout">
        <!-- Left Sidebar - Profile -->
        <aside class="profile-sidebar">
            <div class="avatar-wrapper">
                <img [src]="getUserAvatarUrl()" [alt]="data.user?.name || 'Staff Member'" style="object-fit: cover" />
                <span
                    class="online-indicator"
                    [class.online]="isUserOnline"
                    [title]="isUserOnline ? 'Online' : 'Offline'"
                ></span>
            </div>
            <h3 class="user-name">{{orgUserDetails?.name || data.user?.name || 'Unknown'}}</h3>
            <p class="user-email" *ngIf="userEmail">{{userEmail}}</p>
            <!-- ISS-021: what the person does and where -->
            <p class="ng-hat-location staff-position" *ngFor="let line of positionLines">{{ line }}</p>

            <div class="quick-stats">
                <div class="stat" *ngIf="combinedHatsList.length">
                    <span class="stat-value">{{approvedHatsCount}}</span>
                    <span class="stat-label">Hats</span>
                </div>
                <div class="stat" *ngIf="combinedPositionsList.length">
                    <span class="stat-value">{{combinedPositionsList.length}}</span>
                    <span class="stat-label">Org Posts</span>
                </div>
                <div class="stat" *ngIf="combinedFeaturesList.length">
                    <span class="stat-value">{{aggregatedFeaturesList.length}}</span>
                    <span class="stat-label">Features</span>
                </div>
                <div class="stat" *ngIf="combinedSecurityGroupsList.length">
                    <span class="stat-value">{{securityGroupsList.length}}</span>
                    <span class="stat-label">Groups</span>
                </div>
                <div class="stat" *ngIf="approvedPermissionsCount || pendingPermissionsCount">
                    <span class="stat-value">{{approvedPermissionsCount}}</span>
                    <span class="stat-label">Permissions</span>
                </div>
            </div>

            <div class="pending-stats" *ngIf="hasPendingAssignments">
                <mat-icon class="pending-stats-icon">hourglass_empty</mat-icon>
                <span class="pending-stats-label">Pending:</span>
                <span class="pending-stat" *ngIf="pendingHatsList.length">+{{pendingHatsList.length}} hats</span>
                <span class="pending-stat" *ngIf="pendingPositionsList.length"
                    >+{{pendingPositionsList.length}} org parts</span
                >
                <span class="pending-stat" *ngIf="pendingFeaturesList.length"
                    >+{{pendingFeaturesList.length}} features</span
                >
                <span class="pending-stat" *ngIf="pendingSecurityGroupsList.length"
                    >+{{pendingSecurityGroupsList.length}} groups</span
                >
                <span class="pending-stat" *ngIf="pendingPermissionsCount"
                    >+{{pendingPermissionsCount}} permissions</span
                >
            </div>

            <div class="profile-actions">
                <button mat-stroked-button><mat-icon>chat</mat-icon> Chat</button>
                <button mat-stroked-button><mat-icon>mail</mat-icon> Email</button>
            </div>
        </aside>

        <!-- Main Content -->
        <main class="details-main">
            <!-- 0a. INVITED banner (S1) — user hasn't accepted the org invitation yet.
                 Admin cannot approve any hats until they accept, so this banner replaces
                 the regular Pending Approval CTA. -->
            <section class="detail-section invited-banner-section" *ngIf="awaitingAcceptance">
                <div class="invited-banner">
                    <mat-icon class="invited-banner-icon">mark_email_unread</mat-icon>
                    <div class="invited-banner-text">
                        <h4 class="invited-banner-title">
                            Invited &mdash; awaiting user acceptance
                            <span class="invited-pill">INVITED</span>
                        </h4>
                        <p class="invited-banner-body">
                            An invitation has been sent to
                            <strong>{{userEmail || (orgUserDetails?.name || data.user?.name || 'this user')}}</strong>.
                            They must accept the invitation before any hat assignments can be approved. Once they
                            accept, the <strong>Approve on post</strong> action(s) below will become available.
                        </p>
                    </div>
                </div>
            </section>

            <!-- 0b. Pending Approval Banner + Approve Button -->
            <section class="detail-section collapsible-section pending-section" *ngIf="hasPendingAssignments">
                <div class="section-header-collapsible" (click)="pendingSectionExpanded = !pendingSectionExpanded">
                    <h4 class="section-title">
                        <mat-icon class="expand-icon"
                            >{{pendingSectionExpanded ? 'expand_more' : 'chevron_right'}}</mat-icon
                        >
                        <mat-icon class="pending-icon">hourglass_empty</mat-icon>
                        Pending Approval
                        <span class="count-badge pending-badge">{{pendingHatsList.length}}</span>
                    </h4>
                </div>
                <div class="section-content" *ngIf="pendingSectionExpanded">
                    <p class="pending-info-text">
                        This user is awaiting approval on the post(s) below. Until approved they will
                        <strong>not</strong> wear any of the listed hats. When you approve, you can untick any hats you
                        don&rsquo;t want to assign right now &mdash; they can always be added later via
                        <strong>Manage Hats on Post</strong>. Items marked with
                        <mat-icon class="inline-pending-icon">hourglass_empty</mat-icon> are pending across sections
                        below.
                    </p>

                    <!-- Approve/Reject Buttons (prominent at top) -->
                    <div class="pending-actions" *ngIf="mayManageHats && groupedPendingApprovals.length">
                        <div *ngFor="let group of groupedPendingApprovals" class="approval-action-row">
                            <span class="approval-context">
                                <span class="approval-post-chip" [title]="'Org post: ' + group.positionName">
                                    <mat-icon class="approval-post-icon">work</mat-icon>
                                    <span class="approval-post-label">Post</span>
                                    <span class="approval-post-name">{{ group.positionName }}</span>
                                </span>
                                <span class="approval-hats" *ngIf="group.hatNames.length">
                                    <mat-icon class="inline-icon approval-hat-icon">school</mat-icon>
                                    <span class="approval-hat-chip" *ngFor="let hn of group.hatNames">{{ hn }}</span>
                                </span>
                                <span class="approval-count-hint" *ngIf="group.approvals.length > 1">
                                    ({{ group.approvals.length }} hat requests bundled)
                                </span>
                            </span>
                            <button
                                mat-raised-button
                                color="primary"
                                [disabled]="awaitingAcceptance"
                                [matTooltip]="awaitingAcceptance
                                        ? 'Cannot approve — user has not yet accepted the org invitation.'
                                        : ''"
                                (click)="openGroupedApprovalDialog(group)"
                            >
                                <mat-icon>{{ awaitingAcceptance ? 'mark_email_unread' : 'check_circle' }}</mat-icon>
                                {{ awaitingAcceptance ? 'Awaiting invite acceptance' : 'Approve on post' }}
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            <!-- 1. Hats Worn Section (Collapsible) - includes pending hats -->
            <section class="detail-section collapsible-section" *ngIf="combinedHatsList.length">
                <div class="section-header-collapsible" (click)="hatsSectionExpanded = !hatsSectionExpanded">
                    <h4 class="section-title">
                        <mat-icon class="expand-icon"
                            >{{hatsSectionExpanded ? 'expand_more' : 'chevron_right'}}</mat-icon
                        >
                        <mat-icon class="entity-hats">school</mat-icon>
                        Hats Worn
                        <span class="count-badge entity-hats">{{combinedHatsList.length}}</span>
                    </h4>
                </div>
                <div class="section-content" *ngIf="hatsSectionExpanded">
                    <div class="hats-grid">
                        <div
                            class="hat-chip"
                            [class.is-pending]="hat.isPending"
                            [class.is-board]="hat.isBoard"
                            *ngFor="let hat of combinedHatsList"
                        >
                            <mat-icon>assignment_ind</mat-icon>
                            <span>{{hat.name | normalize}}</span>
                            <span class="ng-hat-location" *ngIf="(hat | hatLocation: data.organization) as hatWhere"
                                >{{ hatWhere }}</span
                            >
                            <mat-icon class="pending-hourglass" *ngIf="hat.isPending">hourglass_empty</mat-icon>
                        </div>
                    </div>
                </div>
            </section>

            <!-- 2. Positions Held Section (Collapsible) - includes pending positions -->
            <section class="detail-section collapsible-section" *ngIf="combinedPositionsList.length">
                <div
                    class="section-header-collapsible"
                    (click)="positionsHeldSectionExpanded = !positionsHeldSectionExpanded"
                >
                    <h4 class="section-title">
                        <mat-icon class="expand-icon"
                            >{{positionsHeldSectionExpanded ? 'expand_more' : 'chevron_right'}}</mat-icon
                        >
                        <mat-icon class="entity-positions">account_tree</mat-icon>
                        Org Posts Held
                        <span class="count-badge entity-positions">{{combinedPositionsList.length}}</span>
                    </h4>
                </div>
                <div class="section-content" *ngIf="positionsHeldSectionExpanded">
                    <div class="positions-grid">
                        <div
                            class="position-chip"
                            [class.is-post]="position.partType === 'post'"
                            [class.is-pending]="position.isPending"
                            [class.is-board]="position.isBoard"
                            *ngFor="let position of combinedPositionsList"
                        >
                            <mat-icon>{{ position.partType === 'post' ? 'person' : 'work' }}</mat-icon>
                            <div class="position-info">
                                <span class="position-name">{{position.name | normalize}}</span>
                                <span class="position-via">via {{position.hatName | normalize}}</span>
                            </div>
                            <mat-icon class="pending-hourglass" *ngIf="position.isPending">hourglass_empty</mat-icon>
                        </div>
                    </div>
                </div>
            </section>

            <!-- 3. Position Lineage Tree Section (Collapsible - collapsed by default) -->
            <section class="detail-section position-lineage-section" *ngIf="hasPositionLineageData">
                <div class="section-header-collapsible" (click)="positionSectionExpanded = !positionSectionExpanded">
                    <h4 class="section-title">
                        <mat-icon class="expand-icon"
                            >{{positionSectionExpanded ? 'expand_more' : 'chevron_right'}}</mat-icon
                        >
                        <mat-icon class="entity-positions">work</mat-icon>
                        Org Part Lineage
                        <span class="section-subtitle">(Org posts held and which hats place you there)</span>
                        <span class="count-badge entity-positions">{{allPositionLineageData.length}}</span>
                    </h4>
                </div>

                <div class="position-lineage-content" *ngIf="positionSectionExpanded">
                    <mat-tree
                        [dataSource]="positionDataSource"
                        [treeControl]="positionTreeControl"
                        class="position-lineage-tree"
                    >
                        <!-- Leaf nodes (no children) -->
                        <mat-tree-node *matTreeNodeDef="let node" matTreeNodePadding>
                            <button mat-icon-button disabled></button>
                            <div class="tree-node" [ngClass]="'node-' + node.type">
                                <mat-icon class="node-icon">{{node.icon}}</mat-icon>
                                <span class="node-name">{{node.name | normalize}}</span>
                            </div>
                        </mat-tree-node>

                        <!-- Expandable nodes (has children) -->
                        <mat-nested-tree-node *matTreeNodeDef="let node; when: hasPositionChild">
                            <div class="mat-tree-node">
                                <button mat-icon-button matTreeNodeToggle [attr.aria-label]="'Toggle ' + node.name">
                                    <mat-icon class="toggle-icon">
                                        {{positionTreeControl.isExpanded(node) ? 'expand_more' : 'chevron_right'}}
                                    </mat-icon>
                                </button>
                                <div class="tree-node" [ngClass]="'node-' + node.type">
                                    <mat-icon class="node-icon">{{node.icon}}</mat-icon>
                                    <span class="node-name">{{node.name | normalize}}</span>
                                    <span class="node-count" *ngIf="node.children?.length">
                                        ({{node.children.length}} hat{{node.children.length === 1 ? '' : 's'}})
                                    </span>
                                </div>
                                <div class="position-hierarchy" *ngIf="node.hierarchy">↳ {{node.hierarchy}}</div>
                            </div>
                            <div class="nested-children" [class.hidden]="!positionTreeControl.isExpanded(node)">
                                <ng-container matTreeNodeOutlet></ng-container>
                            </div>
                        </mat-nested-tree-node>
                    </mat-tree>
                </div>
            </section>

            <!-- 4. Security Groups Section (Collapsible) - includes pending SGs -->
            <section class="detail-section collapsible-section" *ngIf="combinedSecurityGroupsList.length">
                <div
                    class="section-header-collapsible"
                    (click)="securityGroupsSectionExpanded = !securityGroupsSectionExpanded"
                >
                    <h4 class="section-title">
                        <mat-icon class="expand-icon"
                            >{{securityGroupsSectionExpanded ? 'expand_more' : 'chevron_right'}}</mat-icon
                        >
                        <mat-icon class="entity-security-groups">security</mat-icon>
                        Security Groups
                        <span class="count-badge entity-security-groups">{{combinedSecurityGroupsList.length}}</span>
                    </h4>
                </div>
                <div class="section-content" *ngIf="securityGroupsSectionExpanded">
                    <mat-accordion class="groups-accordion">
                        <mat-expansion-panel
                            *ngFor="let sg of combinedSecurityGroupsList"
                            [class.is-pending-panel]="sg.isPending"
                            [expanded]="expandedSecurityGroupPanels.has(sg.sgKey)"
                            (opened)="expandedSecurityGroupPanels.add(sg.sgKey)"
                            (closed)="expandedSecurityGroupPanels.delete(sg.sgKey)"
                        >
                            <mat-expansion-panel-header>
                                <mat-panel-title>
                                    <mat-icon class="pending-hourglass-inline" *ngIf="sg.isPending"
                                        >hourglass_empty</mat-icon
                                    >
                                    {{sg.name | normalize}}
                                </mat-panel-title>
                                <mat-panel-description>
                                    <span *ngIf="sg.isPending && sg.viaHatName"
                                        >via {{sg.viaHatName | normalize}} &middot;
                                    </span>
                                    {{sg.features?.length || 0}} features
                                </mat-panel-description>
                            </mat-expansion-panel-header>
                            <div class="sg-features" *ngIf="sg.features?.length; else noSgFeatures">
                                <div *ngFor="let feature of sg.features" class="feature-row">
                                    <span
                                        class="feature-name"
                                        [matTooltip]="featureDescriptions[feature] || ''"
                                        matTooltipPosition="above"
                                        >{{feature | normalize}}</span
                                    >
                                    <div
                                        class="permissions-badges"
                                        *ngIf="sg.featureDetails?.[feature]?.permissions?.length"
                                    >
                                        <span
                                            class="perm-badge"
                                            *ngFor="let perm of sg.featureDetails[feature].permissions"
                                            [matTooltip]="permissionDescriptions[feature]?.[perm] || ''"
                                            matTooltipPosition="above"
                                        >
                                            {{perm | normalize}}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <ng-template #noSgFeatures>
                                <p class="no-features-msg">No features assigned to this security group.</p>
                            </ng-template>
                        </mat-expansion-panel>
                    </mat-accordion>
                </div>
            </section>

            <!-- 5. Security Group Lineage Tree Section (Collapsible - collapsed by default) -->
            <section class="detail-section security-group-lineage-section" *ngIf="hasSecurityGroupLineageData">
                <div
                    class="section-header-collapsible"
                    (click)="securityGroupSectionExpanded = !securityGroupSectionExpanded"
                >
                    <h4 class="section-title">
                        <mat-icon class="expand-icon"
                            >{{securityGroupSectionExpanded ? 'expand_more' : 'chevron_right'}}</mat-icon
                        >
                        <mat-icon class="entity-security-groups">security</mat-icon>
                        Security Group Lineage
                        <span class="section-subtitle">(Where security groups come from)</span>
                        <span class="count-badge entity-security-groups">{{allSecurityGroupLineageData.length}}</span>
                    </h4>
                </div>

                <div class="security-group-lineage-content" *ngIf="securityGroupSectionExpanded">
                    <mat-tree
                        [dataSource]="securityGroupDataSource"
                        [treeControl]="securityGroupTreeControl"
                        class="security-group-lineage-tree"
                    >
                        <!-- Leaf nodes (no children) -->
                        <mat-tree-node *matTreeNodeDef="let node" matTreeNodePadding>
                            <button mat-icon-button disabled></button>
                            <div class="tree-node" [ngClass]="'node-' + node.type">
                                <mat-icon class="node-icon">{{node.icon}}</mat-icon>
                                <span class="node-name">{{node.name | normalize}}</span>
                            </div>
                            <div class="position-hierarchy" *ngIf="node.hierarchy">↳ {{node.hierarchy}}</div>
                        </mat-tree-node>

                        <!-- Expandable nodes (has children) -->
                        <mat-nested-tree-node *matTreeNodeDef="let node; when: hasSecurityGroupChild">
                            <div class="mat-tree-node">
                                <button mat-icon-button matTreeNodeToggle [attr.aria-label]="'Toggle ' + node.name">
                                    <mat-icon class="toggle-icon">
                                        {{securityGroupTreeControl.isExpanded(node) ? 'expand_more' : 'chevron_right'}}
                                    </mat-icon>
                                </button>
                                <div class="tree-node" [ngClass]="'node-' + node.type">
                                    <mat-icon class="node-icon">{{node.icon}}</mat-icon>
                                    <span class="node-name">{{node.name | normalize}}</span>
                                    <span class="node-count" *ngIf="node.children?.length">
                                        ({{node.children.length}})
                                    </span>
                                </div>
                            </div>
                            <div class="nested-children" [class.hidden]="!securityGroupTreeControl.isExpanded(node)">
                                <ng-container matTreeNodeOutlet></ng-container>
                            </div>
                        </mat-nested-tree-node>
                    </mat-tree>
                </div>
            </section>

            <!-- 6. All Features Section (Collapsible) - includes pending features -->
            <section class="detail-section collapsible-section" *ngIf="combinedFeaturesList.length">
                <div
                    class="section-header-collapsible"
                    (click)="allFeaturesSectionExpanded = !allFeaturesSectionExpanded"
                >
                    <h4 class="section-title">
                        <mat-icon class="expand-icon"
                            >{{allFeaturesSectionExpanded ? 'expand_more' : 'chevron_right'}}</mat-icon
                        >
                        <mat-icon class="entity-features">star</mat-icon>
                        All Features
                        <span class="count-badge entity-features">{{combinedFeaturesList.length}}</span>
                    </h4>
                </div>
                <div class="section-content" *ngIf="allFeaturesSectionExpanded">
                    <mat-chip-set class="features-chips entity-features-chips">
                        <mat-chip
                            *ngFor="let item of combinedFeaturesList"
                            [class.is-pending-chip]="item.isPending"
                            [matTooltip]="item.isPending ? 'Pending approval' : (featureDescriptions[item.feature] || '')"
                            matTooltipPosition="above"
                        >
                            <mat-icon class="pending-hourglass-chip" *ngIf="item.isPending">hourglass_empty</mat-icon>
                            {{item.feature | normalize}}
                        </mat-chip>
                    </mat-chip-set>
                </div>
            </section>

            <!-- 7. Feature Permissions Section (Collapsible) -->
            <section class="detail-section collapsible-section" *ngIf="hasFeatureDetails">
                <div
                    class="section-header-collapsible"
                    (click)="featurePermissionsSectionExpanded = !featurePermissionsSectionExpanded"
                >
                    <h4 class="section-title">
                        <mat-icon class="expand-icon"
                            >{{featurePermissionsSectionExpanded ? 'expand_more' : 'chevron_right'}}</mat-icon
                        >
                        <mat-icon class="entity-permissions">verified_user</mat-icon>
                        Feature Permissions
                        <span class="count-badge entity-permissions">{{featureDetailsList.length}}</span>
                    </h4>
                </div>
                <div class="section-content" *ngIf="featurePermissionsSectionExpanded">
                    <mat-accordion class="permissions-accordion">
                        <mat-expansion-panel
                            *ngFor="let fd of featureDetailsList"
                            [expanded]="expandedFeaturePanels.has(fd.featureKey)"
                            (opened)="expandedFeaturePanels.add(fd.featureKey)"
                            (closed)="expandedFeaturePanels.delete(fd.featureKey)"
                        >
                            <mat-expansion-panel-header>
                                <mat-panel-title
                                    [matTooltip]="featureDescriptions[fd.featureKey] || ''"
                                    matTooltipPosition="above"
                                    >{{fd.featureKey | normalize}}</mat-panel-title
                                >
                                <mat-panel-description
                                    >{{fd.permissions?.length || 0}} permissions</mat-panel-description
                                >
                            </mat-expansion-panel-header>
                            <mat-chip-set class="entity-permissions-chips">
                                <mat-chip
                                    *ngFor="let perm of fd.permissions"
                                    [matTooltip]="permissionDescriptions[fd.featureKey]?.[perm] || ''"
                                    matTooltipPosition="above"
                                    >{{perm | normalize}}</mat-chip
                                >
                            </mat-chip-set>
                        </mat-expansion-panel>
                    </mat-accordion>
                </div>
            </section>

            <!-- 8. Permission Lineage Tree Section (Collapsible - collapsed by default) -->
            <section class="detail-section lineage-section" *ngIf="hasLineageData">
                <div class="section-header-collapsible" (click)="lineageSectionExpanded = !lineageSectionExpanded">
                    <h4 class="section-title">
                        <mat-icon class="expand-icon"
                            >{{lineageSectionExpanded ? 'expand_more' : 'chevron_right'}}</mat-icon
                        >
                        <mat-icon class="entity-permissions">account_tree</mat-icon>
                        Permission Lineage
                        <span class="section-subtitle">(Where access comes from)</span>
                        <span class="count-badge entity-permissions"
                            >{{filteredLineageData.length}}/{{allLineageData.length}}</span
                        >
                    </h4>
                </div>

                <div class="lineage-content" *ngIf="lineageSectionExpanded">
                    <!-- Filter Toggle -->
                    <div class="lineage-filter">
                        <mat-slide-toggle
                            [checked]="showOnlyWithFeatures"
                            (change)="toggleShowOnlyWithFeatures()"
                            color="primary"
                        >
                            Show only hats with features
                        </mat-slide-toggle>
                    </div>

                    <mat-tree [dataSource]="lineageDataSource" [treeControl]="lineageTreeControl" class="lineage-tree">
                        <!-- Leaf nodes (no children) -->
                        <mat-tree-node *matTreeNodeDef="let node" matTreeNodePadding>
                            <button mat-icon-button disabled></button>
                            <div class="tree-node" [ngClass]="'node-' + node.type">
                                <mat-icon class="node-icon">{{node.icon}}</mat-icon>
                                <div class="node-text">
                                    <span class="node-name">{{node.name | normalize}}</span>
                                    <span class="node-hierarchy" *ngIf="node.hierarchy">↳ {{node.hierarchy}}</span>
                                </div>
                            </div>
                        </mat-tree-node>

                        <!-- Expandable nodes (has children) -->
                        <mat-nested-tree-node *matTreeNodeDef="let node; when: hasChild">
                            <div class="mat-tree-node">
                                <button mat-icon-button matTreeNodeToggle [attr.aria-label]="'Toggle ' + node.name">
                                    <mat-icon class="toggle-icon">
                                        {{lineageTreeControl.isExpanded(node) ? 'expand_more' : 'chevron_right'}}
                                    </mat-icon>
                                </button>
                                <div class="tree-node" [ngClass]="'node-' + node.type">
                                    <mat-icon class="node-icon">{{node.icon}}</mat-icon>
                                    <span class="node-name">{{node.name | normalize}}</span>
                                    <span class="node-count" *ngIf="node.children?.length">
                                        ({{node.children.length}})
                                    </span>
                                </div>
                            </div>
                            <div class="nested-children" [class.hidden]="!lineageTreeControl.isExpanded(node)">
                                <ng-container matTreeNodeOutlet></ng-container>
                            </div>
                        </mat-nested-tree-node>
                    </mat-tree>
                </div>
            </section>

            <!-- 9. Policy Acknowledgments Section (Current Policies) -->
            <section
                class="detail-section policies-section collapsible-section"
                *ngIf="!isLoadingPolicies || currentPolicies.length"
            >
                <div class="section-header-collapsible" (click)="policyAckSectionExpanded = !policyAckSectionExpanded">
                    <h4 class="section-title">
                        <mat-icon class="expand-icon"
                            >{{policyAckSectionExpanded ? 'expand_more' : 'chevron_right'}}</mat-icon
                        >
                        <mat-icon class="entity-policies">policy</mat-icon>
                        Policy Acknowledgments
                        <span class="count-badge entity-policies" *ngIf="currentPolicies.length">
                            Approved {{acknowledgedPoliciesCount}} of {{totalPoliciesCount}}
                        </span>
                        <span class="count-badge pending-policies" *ngIf="pendingPoliciesCount > 0">
                            Pending {{pendingPoliciesCount}}
                        </span>
                    </h4>
                </div>

                <div class="section-content" *ngIf="policyAckSectionExpanded">
                    <!-- Loading State -->
                    <div class="loading-policies" *ngIf="isLoadingPolicies">
                        <mat-spinner diameter="20"></mat-spinner>
                        <span>Loading policies...</span>
                    </div>

                    <!-- No Policies -->
                    <div class="no-policies" *ngIf="!isLoadingPolicies && currentPolicies.length === 0">
                        <mat-icon>info_outline</mat-icon>
                        <span>No policies currently assigned to this user</span>
                    </div>

                    <!-- Policy List -->
                    <div class="policies-grid" *ngIf="!isLoadingPolicies && currentPolicies.length > 0">
                        <div
                            class="policy-card"
                            *ngFor="let item of currentPolicies"
                            [class.is-pending]="item.isPending"
                            [ngClass]="{
                               'acknowledged': item.status === 'acknowledged' && !item.isPending,
                               'pending': item.status === 'pending' && !item.isPending,
                               'info-only': item.status === 'not-required' && !item.isPending,
                               'needs-reack': item.acknowledgment?.requiresReacknowledgment
                             }"
                        >
                            <div class="policy-header">
                                <span class="policy-name">
                                    <mat-icon *ngIf="item.isPending" class="pending-hourglass-inline"
                                        >hourglass_empty</mat-icon
                                    >
                                    {{item.policyName}}
                                </span>
                                <div class="policy-chips">
                                    <mat-chip *ngIf="item.isPending" class="status-pending-assignment">
                                        Pending Approval
                                    </mat-chip>
                                    <mat-chip
                                        *ngIf="!item.isPending"
                                        [ngClass]="{
                                      'status-acknowledged': item.status === 'acknowledged',
                                      'status-pending': item.status === 'pending',
                                      'status-info': item.status === 'not-required',
                                      'status-reack': item.acknowledgment?.requiresReacknowledgment
                                    }"
                                    >
                                        {{item.acknowledgment?.requiresReacknowledgment ? 'Re-Ack Required' :
                                        (item.status === 'acknowledged' ? 'Acknowledged' : item.status === 'pending' ?
                                        'Pending' : 'Info Only')}}
                                    </mat-chip>
                                </div>
                            </div>
                            <div class="policy-details">
                                <div class="policy-detail" *ngIf="item.policy?.version">
                                    <mat-icon>description</mat-icon>
                                    <span>Version: {{item.policy?.version}}</span>
                                </div>
                                <div
                                    class="policy-detail"
                                    *ngIf="item.acknowledgment?.requiresReacknowledgment && item.acknowledgment?.newPolicyVersion"
                                >
                                    <mat-icon class="warning-icon">new_releases</mat-icon>
                                    <span class="warning-text"
                                        >New Version: {{item.acknowledgment?.newPolicyVersion}}</span
                                    >
                                </div>
                                <div class="policy-detail" *ngIf="item.acknowledgment?.completedAt">
                                    <mat-icon>check_circle</mat-icon>
                                    <span>Completed: {{formatDate(item.acknowledgment?.completedAt)}}</span>
                                </div>
                                <div class="policy-detail" *ngIf="item.receivedViaHats.length">
                                    <mat-icon>{{item.isPending ? 'hourglass_empty' : 'security'}}</mat-icon>
                                    <span
                                        >Via{{item.isPending ? ' (pending)' : ''}}: {{item.receivedViaHats.join(',
                                        ')}}</span
                                    >
                                </div>
                            </div>
                            <!-- Progress Steps (only for non-pending policies that require acknowledgment) -->
                            <div
                                class="ack-progress"
                                *ngIf="!item.isPending && (item.status === 'pending' || item.acknowledgment?.requiresReacknowledgment)"
                            >
                                <div class="progress-step" [ngClass]="{'completed': item.acknowledgment?.hasRead}">
                                    <mat-icon
                                        >{{item.acknowledgment?.hasRead ? 'check_circle' :
                                        'radio_button_unchecked'}}</mat-icon
                                    >
                                    <span>Read</span>
                                </div>
                                <div
                                    class="progress-step"
                                    [ngClass]="{'completed': item.acknowledgment?.hasUnderstood}"
                                >
                                    <mat-icon
                                        >{{item.acknowledgment?.hasUnderstood ? 'check_circle' :
                                        'radio_button_unchecked'}}</mat-icon
                                    >
                                    <span>Understood</span>
                                </div>
                                <div class="progress-step" [ngClass]="{'completed': item.acknowledgment?.hasAgreed}">
                                    <mat-icon
                                        >{{item.acknowledgment?.hasAgreed ? 'check_circle' :
                                        'radio_button_unchecked'}}</mat-icon
                                    >
                                    <span>Agreed</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- 10. Policy Lineage Tree Section (Current Policies) -->
            <section class="detail-section policy-lineage-section" *ngIf="hasPolicyLineageData">
                <div class="section-header-collapsible" (click)="policySectionExpanded = !policySectionExpanded">
                    <h4 class="section-title">
                        <mat-icon class="expand-icon"
                            >{{policySectionExpanded ? 'expand_more' : 'chevron_right'}}</mat-icon
                        >
                        <mat-icon class="entity-policies">policy</mat-icon>
                        Policy Lineage
                        <span class="section-subtitle">(Where policies come from)</span>
                        <span class="count-badge entity-policies">{{allPolicyLineageData.length}}</span>
                    </h4>
                </div>

                <div class="policy-lineage-content" *ngIf="policySectionExpanded">
                    <mat-tree
                        [dataSource]="policyDataSource"
                        [treeControl]="policyTreeControl"
                        class="policy-lineage-tree"
                    >
                        <!-- Leaf nodes (no children) -->
                        <mat-tree-node *matTreeNodeDef="let node" matTreeNodePadding>
                            <button mat-icon-button disabled></button>
                            <div
                                class="tree-node"
                                [ngClass]="'node-' + node.type"
                                [class.status-acknowledged]="node.status === 'acknowledged'"
                                [class.status-pending]="node.status === 'pending'"
                                [class.status-reack]="node.status === 'requires-reack'"
                            >
                                <mat-icon class="node-icon">{{node.icon}}</mat-icon>
                                <span class="node-name">{{node.name | normalize}}</span>
                                <span class="node-details" *ngIf="node.details">({{node.details}})</span>
                            </div>
                            <div class="position-hierarchy" *ngIf="node.hierarchy">↳ {{node.hierarchy}}</div>
                        </mat-tree-node>

                        <!-- Expandable nodes (has children) -->
                        <mat-nested-tree-node *matTreeNodeDef="let node; when: hasPolicyChild">
                            <div class="mat-tree-node">
                                <button mat-icon-button matTreeNodeToggle [attr.aria-label]="'Toggle ' + node.name">
                                    <mat-icon class="toggle-icon">
                                        {{policyTreeControl.isExpanded(node) ? 'expand_more' : 'chevron_right'}}
                                    </mat-icon>
                                </button>
                                <div
                                    class="tree-node"
                                    [ngClass]="'node-' + node.type"
                                    [class.status-acknowledged]="node.status === 'acknowledged'"
                                    [class.status-pending]="node.status === 'pending'"
                                    [class.status-reack]="node.status === 'requires-reack'"
                                >
                                    <mat-icon class="node-icon">{{node.icon}}</mat-icon>
                                    <span class="node-name">{{node.name | normalize}}</span>
                                    <span class="node-count" *ngIf="node.children?.length">
                                        ({{node.children.length}})
                                    </span>
                                </div>
                            </div>
                            <div class="nested-children" [class.hidden]="!policyTreeControl.isExpanded(node)">
                                <ng-container matTreeNodeOutlet></ng-container>
                            </div>
                        </mat-nested-tree-node>
                    </mat-tree>
                </div>
            </section>

            <!-- 10b. Historical Policy Acknowledgments Section (Collapsed by default) -->
            <section
                class="detail-section policies-section historical-section collapsible-section"
                *ngIf="historicalPolicies.length"
            >
                <div
                    class="section-header-collapsible"
                    (click)="historicalPolicyAckSectionExpanded = !historicalPolicyAckSectionExpanded"
                >
                    <h4 class="section-title">
                        <mat-icon class="expand-icon"
                            >{{historicalPolicyAckSectionExpanded ? 'expand_more' : 'chevron_right'}}</mat-icon
                        >
                        <mat-icon>history</mat-icon>
                        Historical Policy Acknowledgments
                        <span class="section-subtitle">(No longer assigned)</span>
                        <span class="count-badge">{{historicalPolicies.length}}</span>
                    </h4>
                </div>

                <div class="section-content" *ngIf="historicalPolicyAckSectionExpanded">
                    <!-- Policy List -->
                    <div class="policies-grid">
                        <div
                            class="policy-card historical"
                            *ngFor="let item of historicalPolicies"
                            [ngClass]="{
                               'acknowledged': item.status === 'acknowledged',
                               'pending': item.status === 'pending',
                               'info-only': item.status === 'not-required'
                             }"
                        >
                            <div class="policy-header">
                                <span class="policy-name">{{item.policyName}}</span>
                                <div class="policy-chips">
                                    <mat-chip class="status-historical"> Historical </mat-chip>
                                    <mat-chip
                                        [ngClass]="{
                                      'status-acknowledged': item.status === 'acknowledged',
                                      'status-pending': item.status === 'pending',
                                      'status-info': item.status === 'not-required'
                                    }"
                                    >
                                        {{item.status === 'acknowledged' ? 'Acknowledged' : item.status === 'pending' ?
                                        'Pending' : 'Info Only'}}
                                    </mat-chip>
                                </div>
                            </div>
                            <div class="policy-details">
                                <div class="policy-detail" *ngIf="item.policy?.version">
                                    <mat-icon>description</mat-icon>
                                    <span>Version: {{item.policy?.version}}</span>
                                </div>
                                <div class="policy-detail" *ngIf="item.acknowledgment?.completedAt">
                                    <mat-icon>check_circle</mat-icon>
                                    <span>Completed: {{formatDate(item.acknowledgment?.completedAt)}}</span>
                                </div>
                                <div class="policy-detail" *ngIf="item.receivedViaHats.length">
                                    <mat-icon>security</mat-icon>
                                    <span>Was via: {{item.receivedViaHats.join(', ')}}</span>
                                </div>
                                <div class="policy-detail historical-note">
                                    <mat-icon class="info-icon">info</mat-icon>
                                    <span>No longer assigned (removed from targeting hat/role)</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- 10c. Historical Policy Lineage Tree Section (Collapsed by default) -->
            <section
                class="detail-section policy-lineage-section historical-section"
                *ngIf="hasHistoricalPolicyLineageData"
            >
                <div
                    class="section-header-collapsible"
                    (click)="historicalPolicySectionExpanded = !historicalPolicySectionExpanded"
                >
                    <h4 class="section-title">
                        <mat-icon class="expand-icon"
                            >{{historicalPolicySectionExpanded ? 'expand_more' : 'chevron_right'}}</mat-icon
                        >
                        <mat-icon>history</mat-icon>
                        Historical Policy Lineage
                        <span class="section-subtitle">(Where past policies came from)</span>
                        <span class="count-badge">{{allHistoricalPolicyLineageData.length}}</span>
                    </h4>
                </div>

                <div class="policy-lineage-content" *ngIf="historicalPolicySectionExpanded">
                    <mat-tree
                        [dataSource]="historicalPolicyDataSource"
                        [treeControl]="historicalPolicyTreeControl"
                        class="policy-lineage-tree historical-tree"
                    >
                        <!-- Leaf nodes (no children) -->
                        <mat-tree-node *matTreeNodeDef="let node" matTreeNodePadding>
                            <button mat-icon-button disabled></button>
                            <div
                                class="tree-node"
                                [ngClass]="'node-' + node.type"
                                [class.status-acknowledged]="node.status === 'acknowledged'"
                                [class.status-pending]="node.status === 'pending'"
                            >
                                <mat-icon class="node-icon">{{node.icon}}</mat-icon>
                                <span class="node-name">{{node.name | normalize}}</span>
                                <span class="node-details" *ngIf="node.details">({{node.details}})</span>
                            </div>
                            <div class="position-hierarchy" *ngIf="node.hierarchy">↳ {{node.hierarchy}}</div>
                        </mat-tree-node>

                        <!-- Expandable nodes (has children) -->
                        <mat-nested-tree-node *matTreeNodeDef="let node; when: hasHistoricalPolicyChild">
                            <div class="mat-tree-node">
                                <button mat-icon-button matTreeNodeToggle [attr.aria-label]="'Toggle ' + node.name">
                                    <mat-icon class="toggle-icon">
                                        {{historicalPolicyTreeControl.isExpanded(node) ? 'expand_more' :
                                        'chevron_right'}}
                                    </mat-icon>
                                </button>
                                <div
                                    class="tree-node"
                                    [ngClass]="'node-' + node.type"
                                    [class.status-acknowledged]="node.status === 'acknowledged'"
                                    [class.status-pending]="node.status === 'pending'"
                                >
                                    <mat-icon class="node-icon">{{node.icon}}</mat-icon>
                                    <span class="node-name">{{node.name | normalize}}</span>
                                    <span class="node-count" *ngIf="node.children?.length">
                                        ({{node.children.length}})
                                    </span>
                                </div>
                            </div>
                            <div class="nested-children" [class.hidden]="!historicalPolicyTreeControl.isExpanded(node)">
                                <ng-container matTreeNodeOutlet></ng-container>
                            </div>
                        </mat-nested-tree-node>
                    </mat-tree>
                </div>
            </section>

            <!-- 11. Menu Preview Section (Collapsible) - includes pending features -->
            <section class="detail-section collapsible-section" *ngIf="combinedMenuPreview.length">
                <div
                    class="section-header-collapsible"
                    (click)="menuPreviewSectionExpanded = !menuPreviewSectionExpanded"
                >
                    <h4 class="section-title">
                        <mat-icon class="expand-icon"
                            >{{menuPreviewSectionExpanded ? 'expand_more' : 'chevron_right'}}</mat-icon
                        >
                        <mat-icon>menu</mat-icon>
                        Menu Preview
                        <span class="section-subtitle">(What This User Will See)</span>
                        <span class="count-badge">{{combinedMenuPreview.length}}</span>
                    </h4>
                </div>
                <div class="section-content" *ngIf="menuPreviewSectionExpanded">
                    <div class="menu-preview">
                        <div *ngFor="let group of combinedMenuPreview" class="menu-group">
                            <div class="menu-group-header">{{group.section}}</div>
                            <div class="menu-items">
                                <div
                                    *ngFor="let item of group.items"
                                    class="menu-item"
                                    [class.is-pending-menu]="item.isPending"
                                >
                                    <mat-icon>{{item.icon}}</mat-icon>
                                    <span>{{item.name}}</span>
                                    <mat-icon class="pending-hourglass-menu" *ngIf="item.isPending"
                                        >hourglass_empty</mat-icon
                                    >
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- 12. Menu Lineage Tree Section (Collapsible - collapsed by default) -->
            <section class="detail-section menu-lineage-section" *ngIf="hasMenuLineageData">
                <div
                    class="section-header-collapsible"
                    (click)="menuLineageSectionExpanded = !menuLineageSectionExpanded"
                >
                    <h4 class="section-title">
                        <mat-icon class="expand-icon"
                            >{{menuLineageSectionExpanded ? 'expand_more' : 'chevron_right'}}</mat-icon
                        >
                        <mat-icon>account_tree</mat-icon>
                        Menu Lineage
                        <span class="section-subtitle">(Where menu items come from)</span>
                        <span class="count-badge">{{allMenuLineageData.length}}</span>
                    </h4>
                </div>

                <div class="menu-lineage-content" *ngIf="menuLineageSectionExpanded">
                    <mat-tree
                        [dataSource]="menuLineageDataSource"
                        [treeControl]="menuLineageTreeControl"
                        class="menu-lineage-tree"
                    >
                        <!-- Leaf nodes (no children) -->
                        <mat-tree-node *matTreeNodeDef="let node" matTreeNodePadding>
                            <button mat-icon-button disabled></button>
                            <div class="tree-node" [ngClass]="'node-' + node.type">
                                <mat-icon class="node-icon">{{node.icon}}</mat-icon>
                                <span class="node-name">{{node.name | normalize}}</span>
                            </div>
                            <div class="position-hierarchy" *ngIf="node.hierarchy">↳ {{node.hierarchy}}</div>
                        </mat-tree-node>

                        <!-- Expandable nodes (has children) -->
                        <mat-nested-tree-node *matTreeNodeDef="let node; when: hasMenuLineageChild">
                            <div class="mat-tree-node">
                                <button mat-icon-button matTreeNodeToggle [attr.aria-label]="'Toggle ' + node.name">
                                    <mat-icon class="toggle-icon">
                                        {{menuLineageTreeControl.isExpanded(node) ? 'expand_more' : 'chevron_right'}}
                                    </mat-icon>
                                </button>
                                <div class="tree-node" [ngClass]="'node-' + node.type">
                                    <mat-icon class="node-icon">{{node.icon}}</mat-icon>
                                    <span class="node-name">{{node.name | normalize}}</span>
                                    <span class="node-count" *ngIf="node.children?.length">
                                        ({{node.children.length}})
                                    </span>
                                </div>
                            </div>
                            <div class="nested-children" [class.hidden]="!menuLineageTreeControl.isExpanded(node)">
                                <ng-container matTreeNodeOutlet></ng-container>
                            </div>
                        </mat-nested-tree-node>
                    </mat-tree>
                </div>
            </section>

            <!-- No Data -->
            <div
                class="no-data"
                *ngIf="!orgUserDetails?.hats?.length && !hasSecurityGroups && !orgUserDetails?.features?.length && policyAcknowledgments.length === 0"
            >
                <mat-icon>info_outline</mat-icon>
                <p>No additional details available for this user.</p>
            </div>
        </main>
    </div>
</mat-dialog-content>

<mat-dialog-actions align="end">
    <!-- Phone action footer (approved design): Chat tonal + Email primary,
         pinned below the single scroller. Desktop hides these (its copies
         live in the profile sidebar) and keeps the Close button. -->
    <button mat-flat-button class="phone-footer-action phone-footer-chat"><mat-icon>chat</mat-icon> Chat</button>
    <button mat-flat-button class="phone-footer-action phone-footer-email"><mat-icon>mail</mat-icon> Email</button>
    <button mat-flat-button mat-dialog-close color="primary" class="desktop-close-action">Close</button>
</mat-dialog-actions>
